import { sqWorkbookStore } from '@/core/core.stores';
import { useFluxPaths } from '@/core/hooks/useFluxPaths.hook';
import { isApprovalFlowsForOrganizerTopicsEnabled } from '@/services/systemConfiguration.utilities';
import type {
  WorkbookApprovalFlow,
  WorkbookApprovalFlowAssignment,
  WorkbookApprovalFlowStep,
} from '@/worksheet/worksheet.types';

export type UseApprovalFlowsResult = {
  approvalFlow: WorkbookApprovalFlow | null;
  approvalFlowAssignments: WorkbookApprovalFlowAssignment[];
  approvedSteps: WorkbookApprovalFlowStep[] | undefined;
  pendingApprovalSteps: WorkbookApprovalFlowStep[] | undefined;
  nextStep: WorkbookApprovalFlowStep | undefined;
  futureStep: WorkbookApprovalFlowStep | undefined;
  hasApprovalFlowAttached: boolean;
  showApplyBtn: boolean;
  showStepDropdown: boolean;
  workbookId: string;
  approved: boolean | undefined;
};

export const useApprovalFlows = (): UseApprovalFlowsResult => {
  const {
    approvalFlows,
    approvalFlowAssignments,
    approvalFlowAttachments,
    workbookId,
    effectivePermissions,
    isAnalysis,
    isTopic,
    isArchived,
  } = useFluxPaths(sqWorkbookStore, [
    'approvalFlows',
    'approvalFlowAssignments',
    'approvalFlowAttachments',
    'workbookId',
    'effectivePermissions',
    'isAnalysis',
    'isTopic',
    'isArchived',
  ]);
  // GraphQL returns the attached flow plus any other flows the reader can access through a step, newest-first.
  // The header must follow the workbook's attachment, not list order, or it can display and act on an unrelated flow.
  const attachedFlowId = approvalFlowAttachments[0]?.approvalFlowId;
  const approvalFlow =
    (attachedFlowId != null ? approvalFlows.find((flow) => flow.id === attachedFlowId) : approvalFlows[0]) ?? null;
  const pendingApprovalSteps = approvalFlow?.steps.filter(({ status }) => status === 'PENDING');
  const approvedSteps = approvalFlow?.steps.filter(({ status }) => status === 'APPROVED');
  const nextStep = pendingApprovalSteps?.[0];
  const futureStep = pendingApprovalSteps?.[1];
  const hasApprovalFlowAttached = approvalFlowAttachments.length > 0;
  const isSupportedWorkbookType = isAnalysis || (isTopic && isApprovalFlowsForOrganizerTopicsEnabled());
  const showApplyBtn =
    Boolean(approvalFlow) &&
    isSupportedWorkbookType &&
    !isArchived &&
    effectivePermissions.manage &&
    !hasApprovalFlowAttached;
  // The dropdown reports the progress of the flow on this workbook, so it only means anything once the flow is
  // attached: step access alone says nothing about this workbook. Acting on a step still requires write on that step,
  // which the dropdown enforces per option.
  const showStepDropdown = Boolean(approvalFlow) && isSupportedWorkbookType && !isArchived && hasApprovalFlowAttached;
  const approved = approvalFlow?.steps.every((step) => step.status === 'APPROVED');

  return {
    approvedSteps,
    pendingApprovalSteps,
    approvalFlow,
    approvalFlowAssignments,
    hasApprovalFlowAttached,
    nextStep,
    futureStep,
    showApplyBtn,
    showStepDropdown,
    workbookId,
    approved,
  };
};
